/**
 * Card Component - AWLP Design System
 * Container with white background, border, and optional hover effects
 */

import { HTMLAttributes, ReactNode } from 'react';
import styled, { css } from 'styled-components';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  hoverable?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  className?: string;
}

const getPaddingStyles = (padding: 'none' | 'sm' | 'md' | 'lg') => {
  const paddings = {
    none: css`
      padding: 0;
    `,
    sm: css`
      padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
    `,
    md: css`
      padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
    `,
    lg: css`
      padding: ${({ theme }) => `${theme.spacing[5]} ${theme.spacing[6]}`};
    `,
  };

  return paddings[padding];
};

const StyledCard = styled.div<{ $hoverable: boolean; $padding: 'none' | 'sm' | 'md' | 'lg' }>`
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  transition: ${({ theme }) => theme.transitions.fast};

  ${({ $padding }) => getPaddingStyles($padding)}

  ${({ $hoverable }) =>
    $hoverable &&
    css`
      cursor: pointer;

      &:hover {
        border-color: ${({ theme }) => theme.colors.primary.base};
        box-shadow: ${({ theme }) => theme.shadows.md};
        transform: translateY(-2px);
      }

      &:active {
        transform: translateY(-1px);
      }
    `}
`;

export const Card = ({
  children,
  hoverable = false,
  padding = 'md',
  className,
  ...props
}: CardProps) => {
  return (
    <StyledCard $hoverable={hoverable} $padding={padding} className={className} {...props}>
      {children}
    </StyledCard>
  );
};

export default Card;
